/**
 * Normalize backend/internal progress messages into user-facing loading text.
 *
 * Regex is used because progress messages may include dynamic details such as
 * file names, progress counts, job type names, or OCR markers.
 */
const REPLACEMENTS: Array<[RegExp, string]> = [
  // Keep mappings explicit so they can be checked against the agreed As-Is/To-Be wording.
  [/^\s*Queued:\s*searching patents\s*$/i, '条件に合う特許を収集する準備をしています'],
  [
    /^\s*Queued:\s*extracting realization methods\s*$/i,
    '特許マップに載せる技術のまとまりを整理する準備をしています',
  ],
  [/^\s*Queued:\s*extracting components\s*$/i, '各技術を構成する要素を整理する準備をしています'],
  [/^\s*Queued:\s*judging patent quality\s*$/i, '特許マップ上での重要度を評価する準備をしています'],
  [
    /^\s*Classifying keywords to IPC\s*$/i,
    '入力内容と特許例から、関連する技術分野を整理しています',
  ],
  [
    /^\s*Feedback expansion\s*\/\s*IPC refinement\s*$/i,
    '関連する技術分野を広げ、検索に使う候補を整えています',
  ],
  [
    /^\s*フィードバック拡張\s*\/\s*IPC精査中\s*$/i,
    '関連する技術分野を広げ、検索に使う候補を整えています',
  ],
  [
    /^\s*フィードバック機能によるIPC収集\s*$/i,
    '関連する技術分野を広げ、検索に使う候補を整えています',
  ],
  [/^\s*IPCへのキーワード分類\s*$/i, '入力内容と特許例から、関連する技術分野を整理しています'],
  [/^\s*キーワードをIPCに分類中\s*$/i, '入力内容と特許例から、関連する技術分野を整理しています'],
  [/^\s*Executing final search\s*$/i, '条件に合う特許を収集しています'],
  [/^\s*Processing\s*$/i, '処理を進めています'],
  [/^\s*PDFをダウンロード中.*$/i, '特許例の内容を読み取る準備をしています'],
  [/^\s*PDF解析中.*$/i, '特許例の内容を読み取っています'],
  [/^\s*PDF解析待機中.*$/i, '特許例の読み取りを開始する準備をしています'],
  [/^\s*検索式生成を開始しました\s*$/i, '特許を集めるための検索条件を作成しています'],
  [/^\s*検索式生成開始\s*$/i, '特許を集めるための検索条件を作成しています'],
  [/^\s*検索式生成\s*作業中\s*$/i, '特許を集めるための検索条件を作成しています'],
  [/^\s*検索式の生成\s*$/i, '特許を集めるための検索条件を作成しています'],
  [/^\s*検索式のヒット件数取得\s*\(SerpAPI\)\s*$/i, '検索条件ごとに、集まる特許数を確認しています'],
  [/^\s*検索式のスコアリング\s*\(LLM\)\s*$/i, '検索条件の候補を比較し、使いやすい順に整えています'],
  [/^\s*前処理待機中.*$/i, '特許例の読み取り結果を準備しています'],
  [/^\s*検索式生成が完了しました\s*$/i, '検索条件の作成が完了しました'],
  [/^\s*検索式生成完了\s*$/i, '検索条件の作成が完了しました'],
  [/^\s*特許検索を実行中.*$/i, '条件に合う特許を収集しています'],
  [/^\s*特許検索中.*$/i, '条件に合う特許を収集しています'],
  [/^\s*検索式\s*\d+\s*\/\s*\d+\s*を実行中.*$/i, '条件に合う特許を収集しています'],
  [/^\s*検索式 .* を実行中.*$/i, '条件に合う特許を収集しています'],
  [/^\s*特許検索が完了しました\s*$/i, '特許の収集が完了しました'],
  [/^\s*実現方法_中分類を抽出中.*$/i, '特許マップに載せる技術のまとまりを整理しています'],
  [/^\s*実現方法を抽出中.*$/i, '特許マップに載せる技術のまとまりを整理しています'],
  [/^\s*実現方法の抽出が完了しました\s*$/i, '技術のまとまりの整理が完了しました'],
  [/^\s*構成要素を抽出中.*$/i, '各技術を構成する要素を整理しています'],
  [/^\s*構成要素の抽出が完了しました\s*$/i, '技術を構成する要素の整理が完了しました'],
  [/^\s*特許品質を評価中.*$/i, '特許マップ上での重要度を評価しています'],
  [/^\s*戦略を生成中.*$/i, '特許マップ上での重要度評価の結果を整理しています'],
  [/^\s*特許品質評価が完了しました\s*$/i, '重要度の評価が完了しました'],
  [/^\s*Excelを生成中.*$/i, '特許マップのExcelファイルを作成しています'],
  [/^\s*Excel生成中\s*$/i, '特許マップのExcelファイルを作成しています'],
  [/^\s*Excel生成が完了しました\s*$/i, 'Excelファイルの作成が完了しました'],
  [/^\s*処理を開始しました（pdf_analyze）\s*$/i, '特許例の内容を読み取っています'],
  [
    /^\s*処理を開始しました（generate_formulas）\s*$/i,
    '特許を集めるための検索条件を作成しています',
  ],
  [/^\s*処理を開始しました（execute_final_search）\s*$/i, '条件に合う特許を収集しています'],
  [
    /^\s*処理を開始しました（extract_realization_methods）\s*$/i,
    '特許マップに載せる技術のまとまりを整理しています',
  ],
  [/^\s*処理を開始しました（extract_components）\s*$/i, '各技術を構成する要素を整理しています'],
  [/^\s*処理を開始しました（judge_patent_quality）\s*$/i, '特許マップ上での重要度を評価しています'],
  [/^\s*処理を開始しました（generate_excel）\s*$/i, '特許マップのExcelファイルを作成しています'],
  [/^\s*キュー投入:\s*PDF解析（再実行）\s*$/i, '特許例の読み取りを開始する準備をしています'],
  [/^\s*キュー投入:\s*PDF解析\s*$/i, '特許例の読み取りを開始する準備をしています'],
  [/^\s*キュー投入:\s*検索式生成\s*$/i, '検索条件の作成を開始する準備をしています'],
  [/^\s*検索式生成待機中.*$/i, '検索条件の作成を開始する準備をしています'],
  [/^\s*キュー投入:\s*特許検索\s*$/i, '条件に合う特許を収集する準備をしています'],
  [
    /^\s*キュー投入:\s*実現方法抽出\s*$/i,
    '特許マップに載せる技術のまとまりを整理する準備をしています',
  ],
  [/^\s*キュー投入:\s*構成要素抽出\s*$/i, '各技術を構成する要素を整理する準備をしています'],
  [/^\s*キュー投入:\s*特許品質評価\s*$/i, '特許マップ上での重要度を評価する準備をしています'],
  [/^\s*キュー投入:\s*Excel生成\s*$/i, '特許マップのExcelファイルを作成する準備をしています'],
];

/**
 * Return a user-facing progress message when a backend/internal pattern is known.
 * Unknown strings are returned unchanged so new messages remain visible.
 */
export function normalizeProgressMessage(raw: string): string {
  const s = (raw || '').toString();
  if (!s) return s;

  // Preserve base message, but normalize OCR marker if present inside filename.
  const normalized = s.replace(/\[OCR\]/g, 'OCR');

  for (const [re, repl] of REPLACEMENTS) {
    if (re.test(normalized)) return repl;
  }

  return normalized;
}
